import { IHolds } from '@/redux/reducers/web3/web3.type'

import { w3Api } from '../w3Api'
import { W3ResponseType } from '../events'
import { INotification, ParamsFilterNoti } from './notification.type'

export const NotificationEndpoints = {
  getNotifcation: '/notification',
  readNotification: '/notification/read/:id',
  readAllNotification: '/notification/read-all',
  getHoldings: '/key/holding',
  updateToken: '/user/update-device-token',
  updateNotification: '/notification/:id',
}

export const NotificationlApi = w3Api.injectEndpoints({
  endpoints: (build) => ({
    //notification
    getNotification: build.query<any, ParamsFilterNoti>({
      query: (args) => {
        return {
          url: NotificationEndpoints.getNotifcation,
          params: args,
        }
      },
      providesTags: (): any => [{ type: 'Notifications', isCount: true }],
    }),

    getInfiniteNotification: build.query<W3ResponseType<INotification[]>, ParamsFilterNoti>({
      query: (args) => {
        const queryString = new URLSearchParams()

        Object.entries(args)
          .filter((obj: any) => {
            return obj?.[1] !== undefined // get value
          })
          .forEach(([key, value]) => {
            if (Array.isArray(value)) {
              value.forEach((v) => queryString.append(`${key}[]`, v))
            } else {
              queryString.append(key, value)
            }
          })

        return {
          url: `${NotificationEndpoints.getNotifcation}?${queryString.toString()}`,
        }
      },
      providesTags: (result, err, arg): any =>
        result
          ? [
              ...(Array.isArray(result.data)
                ? result.data.map(({ id }: { id: string }) => ({ type: 'Notifications' as const, id: id }))
                : []),
              { type: 'Notifications', searchType: arg.type ? arg.type.join(',') : undefined },
              { type: 'Notifications' },
            ]
          : [
              { type: 'Notifications', searchType: arg.type ? arg.type.join(',') : undefined },
              { type: 'Notifications' },
            ],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}-${queryArgs.type ? queryArgs.type.join(',') : undefined}`
      },
      merge: (currentCache, newItems) => {
        if (newItems.meta.page === 0) {
          return newItems
        }
        return {
          ...currentCache,
          data: [...currentCache.data, ...newItems.data],
          meta: {
            hasNext: newItems.meta.hasNext,
            hasPrev: newItems.meta.hasPrev,
            limit: newItems.meta.limit,
            page: newItems.meta.page,
            totalItem: newItems.meta.totalItem,
            totalPage: newItems.meta.totalPage,
          },
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg
      },
    }),
    readNotification: build.mutation<any, string>({
      query: (args) => {
        return {
          url: NotificationEndpoints.readNotification.replace(':id', args),
          method: 'PUT',
        }
      },
      invalidatesTags() {
        return [{ type: 'Notifications', isCount: true }]
      },
    }),
    readAllNotification: build.mutation<any, void>({
      query: () => {
        return {
          url: NotificationEndpoints.readAllNotification,
          method: 'PUT',
        }
      },
      invalidatesTags() {
        return [{ type: 'Notifications', isCount: true }]
      },
    }),

    getNotificationId: build.query<any, { id: number }>({
      query: (args) => {
        return {
          url: `${NotificationEndpoints.getNotifcation}/${args.id}`,
          method: 'GET',
        }
      },
    }),
    getHoldings: build.query<IHolds, { userId: string }>({
      query: (args) => {
        return {
          url: `${NotificationEndpoints.getHoldings}`,
          method: 'GET',
          params: { influencerId: args.userId },
        }
      },
    }),
    updateCurrentToken: build.mutation<any, string>({
      query: (args) => {
        return {
          url: NotificationEndpoints.updateToken,
          method: 'PATCH',
          body: { deviceToken: args },
        }
      },
      invalidatesTags() {
        return [{ type: 'Notifications', isCount: true }]
      },
    }),
    updateNotification: build.mutation<any, { id: string; rqCoHostStatus: 'rejected' | 'approved' }>({
      query: (args) => {
        return {
          url: NotificationEndpoints.updateNotification.replace(':id', args.id),
          method: 'PUT',
          body: { rqCoHostStatus: args.rqCoHostStatus },
        }
      },
    }),
  }),
  overrideExisting: true,
})

export const {
  useGetNotificationQuery,
  useGetNotificationIdQuery,
  useGetHoldingsQuery,
  useLazyGetHoldingsQuery,
  useGetInfiniteNotificationQuery,
  useReadAllNotificationMutation,
  useReadNotificationMutation,
  useUpdateCurrentTokenMutation,
  useUpdateNotificationMutation,
} = NotificationlApi
